import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { useAtomValue } from 'jotai';
import { InformationCircleIcon } from '@heroicons/react/24/outline';

import { WorkerReference } from '@restworld/utility-types';
import { formatDateToDayMonthYear } from '@restworld/utils-common';
import { IconButton, Tooltip } from '@restworld/ui-ds-base';

import { selectedJobApplicationAtom } from '../../../../employer-job-listing.atoms';

function CandidateReferenceItem({ reference = {} as WorkerReference }: CandidateReferenceItemProps) {
  const { t } = useTranslation('common');
  const { message, referent_name, referent_surname, workplace_relationship, inserted_at, worker_past_experience } =
    reference;
  const { occupation, from_date, to_date, restaurant_name } = worker_past_experience || {};
  const selectedJobApplication = useAtomValue(selectedJobApplicationAtom);

  const tooltipContent = useMemo(
    () =>
      t('reference_additional_info_key', {
        workerName: selectedJobApplication?.worker?.name || '',
        referrantName: referent_name,
        occupation: occupation?.label_it.label,
        fromDate: formatDateToDayMonthYear(from_date),
        toDate: formatDateToDayMonthYear(to_date),
      }),
    [from_date, occupation?.label_it.label, referent_name, selectedJobApplication?.worker?.name, t, to_date]
  );

  return (
    <div className="rounded-2xl border border-neutral-40 p-4">
      <div className="flex justify-between items-center">
        <h5 className="text-base font-semibold text-neutral-700">
          <span>{`${referent_name || ''} ${referent_surname || ''},`}</span>{' '}
          <span className="capitalize">{t(workplace_relationship)}</span> <span className="font-normal">{t('at')}</span>{' '}
          <span>{restaurant_name || ''}</span>
        </h5>
        <Tooltip content={tooltipContent} placement="bottom">
          <IconButton ariaLabel={t('view_info')} Icon={InformationCircleIcon} size="sm" />
        </Tooltip>
      </div>
      <span className="text-neutral-200 block mt-1 mb-[14px]">
        {t('sent_on_key')} {formatDateToDayMonthYear(inserted_at)}
      </span>
      <div className="bg-purple-50 rounded-lg p-4 text-neutral-700">{`"${message}"`}</div>
    </div>
  );
}

export default CandidateReferenceItem;

interface CandidateReferenceItemProps {
  reference: WorkerReference;
}
